/** @jsx jsx */
import { jsx } from '@emotion/react'
import { useEffect, useState } from 'react'

import CourseAutoComplete from './CourseAutoComplete'
import useEnvironment from '../../../lib/hooks/useEnvironment'
import { CatalogSearchResultFragment, useSearchCoursesQuery } from '../../../generated/graphql'
import useNotification from '../../../lib/hooks/useNotification'
import useAppSettings from '../../../lib/hooks/useAppSettings'

type CourseAutoCompleteWrapperProps = {
  /** Campus used to filter courses */
  campus?: string
  /** If the selected course can be cleared */
  clearable?: boolean
  /** Whether the input is disabled */
  disabled?: boolean
  /** Current Group Id */
  groupId: string
  /** If the dropdown should allow selecting multiple options */
  isMultiSelect?: boolean
  /** Label of Autocomplete. Defaults to Item */
  label?: string
  /** Event callback when a new Catalog Course is selected or placeholder is added */
  onChange: (course: CatalogSearchResultFragment | CatalogSearchResultFragment[]) => void
  /** Optional override for placeholder text in dropdown */
  placeholder?: string
  /** Selected course for placeholder */
  selectedCourse?: CatalogSearchResultFragment | undefined
  /** Whether to show static option at bottom of list */
  showStaticOption?: boolean
  /** Term Code used for finding active courses for this term */
  termCode?: string
  /** Composite key of SubjectID and CourseNumber */
  value?: string | CatalogSearchResultFragment[]
}

const defaultProps: Pick<
  CourseAutoCompleteWrapperProps,
  | 'campus'
  | 'clearable'
  | 'disabled'
  | 'isMultiSelect'
  | 'label'
  | 'placeholder'
  | 'selectedCourse'
  | 'showStaticOption'
  | 'termCode'
  | 'value'
> = {
  campus: undefined,
  clearable: false,
  disabled: false,
  isMultiSelect: false,
  label: 'Item',
  placeholder: undefined,
  selectedCourse: undefined,
  showStaticOption: false,
  termCode: undefined,
  value: ''
}

export const CourseAutoCompleteWrapper = ({
  campus,
  clearable,
  disabled,
  groupId,
  isMultiSelect,
  label,
  placeholder,
  onChange,
  selectedCourse,
  showStaticOption,
  termCode,
  value
}: CourseAutoCompleteWrapperProps) => {
  const [inputValue, setInputValue] = useState('')
  const [catalogCourses, setCatalogCourses] = useState<CatalogSearchResultFragment[]>([])
  const environment = useEnvironment()
  const { showErrorNotif } = useNotification()
  const {
    text: { coursePlural }
  } = useAppSettings()

  const { data } = useSearchCoursesQuery({
    variables: {
      campus,
      text: inputValue,
      environment,
      termCode
    },
    // skip query if text is empty
    skip: !inputValue,
    context: { useApolloNetworkStatus: false },
    onError: () => showErrorNotif(`Could not search ${coursePlural.toLowerCase()}.`)
  })

  const handleInputChange = (inputVal: string) => {
    setInputValue(inputVal)
  }

  useEffect(() => {
    /**
     * This effect avoids re-rendering the menu when query is loading.
     * data is undefined when the query is loading.
     * Thus if we use query.data directly, the menu renders as empty when query is loading.
     */
    if (data) {
      setCatalogCourses(data.searchCourses)
    }
  }, [data])

  return (
    <CourseAutoComplete
      id="course-autocomplete"
      // Hack to display the selected course when opening a plan item.  When opening a saved placeholder plan item
      // with a course, because nothing has been typed into the auto complete yet, there are no options in the dropdown
      // to match on to be able to display the selected course.  Use the saved course as the options in the dropdown
      // if there aren't any other options.
      catalogCourses={
        !catalogCourses?.length && selectedCourse && value ? [selectedCourse] : catalogCourses
      }
      clearable={clearable}
      disabled={disabled}
      groupId={groupId}
      inputValue={inputValue}
      isMultiSelect={isMultiSelect}
      label={label}
      onChange={onChange}
      placeholder={placeholder}
      onInputChange={handleInputChange}
      showStaticOption={showStaticOption}
      value={value}
    />
  )
}

CourseAutoCompleteWrapper.defaultProps = defaultProps

export default CourseAutoCompleteWrapper
